/**
 * Organization documents API – public list + super-admin management.
 */

import { handleApiUnauthorized } from '@utils/apiUnauthorized';
import type {
  ApiResponse,
  CreateOrganizationDocumentRequest,
  ListOrganizationDocumentsResponse,
  ListPublicOrganizationDocumentsResponse,
  OrganizationDocument,
  PublicOrganizationDocument,
  UpdateOrganizationDocumentRequest,
} from '@lambda/shared/types';
import { accountApi } from './accountApi';
import { apiFetchUrl, assertApiConfigured, getApiBaseUrl } from '@constants/apiConfig';

async function authRequest<T>(
  path: string,
  accessToken: string,
  options: RequestInit & { payload?: object }
): Promise<ApiResponse<T>> {
  assertApiConfigured();
  const { payload, ...rest } = options;
  const res = await fetch(apiFetchUrl(path), {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      ...(options.headers as Record<string, string>),
    },
    body: payload ? JSON.stringify(payload) : undefined,
  });
  const data = (await res.json().catch(() => ({}))) as ApiResponse<T>;
  if (handleApiUnauthorized(res.status, data)) {
    throw new Error(data?.error?.message ?? 'Session expired. Please log in again.');
  }
  if (!res.ok) {
    throw new Error(data?.error?.message ?? `Request failed (${res.status})`);
  }
  return data;
}

async function publicRequest<T>(path: string): Promise<ApiResponse<T>> {
  assertApiConfigured();
  const res = await fetch(apiFetchUrl(path));
  const data = (await res.json().catch(() => ({}))) as ApiResponse<T>;
  if (!res.ok) {
    throw new Error(data?.error?.message ?? `Request failed (${res.status})`);
  }
  return data;
}

export function organizationDocumentDownloadUrl(documentId: string): string {
  if (import.meta.env.PROD && !getApiBaseUrl()) return '#';
  return apiFetchUrl(`/public/organization-documents/${documentId}/download`);
}

export async function openOrganizationDocument(documentId: string): Promise<void> {
  assertApiConfigured();
  const res = await fetch(
    apiFetchUrl(`/public/organization-documents/${documentId}/download?format=json`)
  );
  const data = (await res.json().catch(() => ({}))) as ApiResponse<{ downloadUrl: string }>;
  if (!res.ok) {
    throw new Error(data?.error?.message ?? `Could not open document (${res.status})`);
  }
  const url = data?.data?.downloadUrl;
  if (!url) {
    throw new Error('Document download link is unavailable.');
  }
  const opened = window.open(url, '_blank', 'noopener,noreferrer');
  if (!opened) {
    throw new Error('Pop-up blocked. Allow pop-ups for this site and try again.');
  }
}

export const organizationDocumentsApi = {
  listPublic() {
    return publicRequest<ListPublicOrganizationDocumentsResponse>('/public/organization-documents');
  },

  list(accessToken: string) {
    return authRequest<ListOrganizationDocumentsResponse>('/organization-documents', accessToken, {
      method: 'GET',
    });
  },

  create(accessToken: string, data: CreateOrganizationDocumentRequest) {
    return authRequest<{ document: OrganizationDocument; message: string }>(
      '/organization-documents',
      accessToken,
      { method: 'POST', payload: data }
    );
  },

  update(accessToken: string, documentId: string, data: UpdateOrganizationDocumentRequest) {
    return authRequest<{ document: OrganizationDocument; message: string }>(
      `/organization-documents/${documentId}`,
      accessToken,
      { method: 'PUT', payload: data }
    );
  },

  delete(accessToken: string, documentId: string) {
    return authRequest<{ message: string }>(`/organization-documents/${documentId}`, accessToken, {
      method: 'DELETE',
    });
  },

  uploadRequest(
    accessToken: string,
    documentId: string,
    fileName: string,
    fileType: string,
    fileSize: number
  ) {
    return authRequest<{ uploadUrl: string; fileKey: string; message: string }>(
      `/organization-documents/${documentId}/upload-request`,
      accessToken,
      { method: 'POST', payload: { fileName, fileType, fileSize } }
    );
  },

  confirmUpload(accessToken: string, documentId: string) {
    return authRequest<{ document: OrganizationDocument; message: string }>(
      `/organization-documents/${documentId}/confirm-upload`,
      accessToken,
      { method: 'POST', payload: {} }
    );
  },

  async uploadPdf(accessToken: string, documentId: string, file: File): Promise<OrganizationDocument> {
    const req = await this.uploadRequest(
      accessToken,
      documentId,
      file.name,
      file.type || 'application/pdf',
      file.size
    );
    const uploadUrl = req.data?.uploadUrl;
    if (!uploadUrl) throw new Error('Invalid upload response');
    await accountApi.uploadToPresignedUrl(uploadUrl, file, file.type || 'application/pdf');
    const confirmed = await this.confirmUpload(accessToken, documentId);
    if (!confirmed.data?.document) throw new Error('Confirm upload failed');
    return confirmed.data.document;
  },
};

export type { PublicOrganizationDocument };
